import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { useFileDropZone } from "@components/shared/FileDropZone";
import { useSpeechRecognition } from "@hooks/useSpeechRecognition";
import {
  ACCEPTED_CONTENT_TYPES,
  MAX_FILE_SIZE,
  MAX_FILES_PER_MESSAGE,
  uploadFile,
} from "@lib/chatApi";
import { formatBytes } from "@lib/formatBytes";
import { htmlToMarkdown } from "@lib/htmlToMarkdown";
import type { ChatAttachment, ChatRichTextArtefact } from "@lib/types";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Mic,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

function estimateTokens(att: ChatAttachment): number | null {
  if (!att.size) return null;
  if (att.content_type.startsWith("image/")) {
    // Images are resized/tiled by the API; rough estimate ~1600 tokens for typical images
    return 1600;
  }
  // Documents: ~1 token per 4 bytes of base64 (base64 inflates by ~1.33x)
  return Math.ceil((att.size * 1.33) / 4);
}

function formatTokenEstimate(tokens: number): string {
  if (tokens >= 1000) return `~${Math.round(tokens / 1000)}k tokens`;
  return `~${tokens} tokens`;
}

interface ChatInputProps {
  onSend: (
    message: string,
    attachments: ChatAttachment[],
    richTextArtefacts: ChatRichTextArtefact[],
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Pre-fills the input. Changes to this prop overwrite the current draft. */
  initialValue?: string;
  /** Slot rendered right-aligned in the controls row above the input. */
  trailingControl?: React.ReactNode;
}

// Counter so each pasted artefact gets a stable React key + expand-state key
// during this composer's lifetime. Backend regenerates ids on persist.
let pastedArtefactCounter = 0;

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = "Type a message...",
  initialValue,
  trailingControl,
}: ChatInputProps) {
  const [value, setValue] = useState(initialValue ?? "");
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [richTextArtefacts, setRichTextArtefacts] = useState<ChatRichTextArtefact[]>([]);
  const [expandedArtefactIds, setExpandedArtefactIds] = useState<Set<string>>(new Set());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isListening, isSupported, transcript, start, stop } = useSpeechRecognition();

  useEffect(() => {
    if (transcript) {
      setValue((prev) => (prev ? `${prev} ${transcript}` : transcript));
    }
  }, [transcript]);

  useEffect(() => {
    if (initialValue === undefined) return;
    setValue(initialValue);
    const el = textareaRef.current;
    if (el) {
      el.focus();
      const len = initialValue.length;
      el.setSelectionRange(len, len);
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  }, [initialValue]);

  const uploadedAttachments = attachments.filter((a) => a.signed_id && !a.error && !a.uploading);
  const hasUploading = attachments.some((a) => a.uploading);
  const canSend =
    !disabled &&
    !hasUploading &&
    (!!value.trim() || uploadedAttachments.length > 0 || richTextArtefacts.length > 0);

  function handleSubmit() {
    if (!canSend) return;
    onSend(value.trim(), attachments, richTextArtefacts);
    setValue("");
    setAttachments([]);
    setRichTextArtefacts([]);
    setExpandedArtefactIds(new Set());
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleInput() {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  }

  async function processFiles(files: File[]) {
    const remaining = MAX_FILES_PER_MESSAGE - attachments.length;
    const toProcess = files.slice(0, remaining);

    for (const file of toProcess) {
      if (!ACCEPTED_CONTENT_TYPES.includes(file.type)) continue;
      if (file.size > MAX_FILE_SIZE) continue;

      const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;

      const newAttachment: ChatAttachment = {
        signed_id: "",
        filename: file.name,
        content_type: file.type,
        size: file.size,
        preview,
        uploading: true,
      };

      setAttachments((prev) => [...prev, newAttachment]);

      try {
        const result = await uploadFile(file);
        setAttachments((prev) =>
          prev.map((a) =>
            a.filename === file.name && a.uploading
              ? { ...a, signed_id: result.signed_id, uploading: false }
              : a,
          ),
        );
      } catch {
        setAttachments((prev) =>
          prev.map((a) =>
            a.filename === file.name && a.uploading
              ? { ...a, uploading: false, error: "Upload failed" }
              : a,
          ),
        );
      }
    }
  }

  const {
    rootProps: dropZoneProps,
    inputProps: fileInputProps,
    dragging: isDragging,
    openPicker: openFilePicker,
  } = useFileDropZone({
    accept: ACCEPTED_CONTENT_TYPES.join(","),
    multiple: true,
    disabled: disabled || attachments.length >= MAX_FILES_PER_MESSAGE,
    onFiles: processFiles,
  });

  function handlePaste(e: React.ClipboardEvent) {
    // Image / file paste: existing flow.
    const pastedFiles = Array.from(e.clipboardData.files);
    if (pastedFiles.length > 0) {
      e.preventDefault();
      processFiles(pastedFiles);
      return;
    }

    // Rich-text paste: capture HTML, convert to markdown, attach as artefact.
    // Plain-text-only pastes fall through to the textarea unchanged.
    const html = e.clipboardData.getData("text/html");
    if (!html || !html.trim()) return;

    const markdown = htmlToMarkdown(html);
    if (!markdown) return;

    e.preventDefault();
    const sizeBytes = new Blob([markdown]).size;
    pastedArtefactCounter += 1;
    const artefact: ChatRichTextArtefact = {
      id: `paste-${Date.now()}-${pastedArtefactCounter}`,
      label: "Pasted document",
      size_bytes: sizeBytes,
      markdown,
    };
    setRichTextArtefacts((prev) => [...prev, artefact]);
  }

  function removeArtefact(id: string) {
    setRichTextArtefacts((prev) => prev.filter((a) => a.id !== id));
    setExpandedArtefactIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function toggleArtefactExpanded(id: string) {
    setExpandedArtefactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }

  function addDriveAttachment(signedId: string, filename: string, contentType: string | null) {
    setAttachments((prev) => [
      ...prev,
      {
        signed_id: signedId,
        filename,
        content_type: contentType ?? "application/octet-stream",
        size: 0,
        uploading: false,
      },
    ]);
  }

  return (
    <div
      {...dropZoneProps}
      className={`relative border-t border-border bg-surface transition-colors ${
        isDragging ? "border-t-accent bg-accent/5" : ""
      }`}
    >
      {/* Rich-text artefact pills (collapsible markdown previews) */}
      {richTextArtefacts.length > 0 && (
        <div className="flex flex-col gap-2 px-3 pt-3">
          {richTextArtefacts.map((artefact) => {
            const isExpanded = expandedArtefactIds.has(artefact.id);
            return (
              <div key={artefact.id} className="flex flex-col gap-1">
                <div className="relative group inline-flex self-start">
                  <button
                    type="button"
                    onClick={() => toggleArtefactExpanded(artefact.id)}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-border bg-surface-secondary text-xs text-text-secondary max-w-[260px] hover:bg-surface"
                    title={isExpanded ? "Collapse pasted content" : "Expand pasted content"}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <ChevronDown size={12} className="flex-shrink-0" />
                    ) : (
                      <ChevronRight size={12} className="flex-shrink-0" />
                    )}
                    <FileText size={14} className="flex-shrink-0" />
                    <span className="truncate">
                      {artefact.label} · {formatBytes(artefact.size_bytes)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeArtefact(artefact.id)}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-surface-secondary border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error hover:text-white hover:border-error"
                    title="Remove pasted document"
                    aria-label="Remove pasted document"
                  >
                    <X size={10} />
                  </button>
                </div>
                {isExpanded && (
                  <div className="rounded-md border border-border bg-surface-secondary p-3 text-xs text-text-primary prose prose-sm prose-neutral dark:prose-invert max-w-none">
                    <Markdown remarkPlugins={[remarkGfm]}>{artefact.markdown}</Markdown>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="flex gap-2 px-3 pt-3 overflow-x-auto">
          {attachments.map((att, i) => {
            const tokens = estimateTokens(att);
            return (
              <div
                key={att.signed_id || att.filename}
                className="flex flex-col items-center flex-shrink-0"
              >
                <div className="relative group">
                  {att.content_type.startsWith("image/") ? (
                    <div className="w-16 h-16 rounded-md overflow-hidden border border-border bg-surface-secondary">
                      {att.preview && (
                        <img
                          src={att.preview}
                          alt={att.filename}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-border bg-surface-secondary text-xs text-text-secondary max-w-[120px]">
                      <FileText size={14} className="flex-shrink-0" />
                      <span className="truncate">{att.filename}</span>
                    </div>
                  )}
                  {att.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-md">
                      <Loader2 size={16} className="animate-spin text-white" />
                    </div>
                  )}
                  {att.error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 rounded-md">
                      <span className="text-[10px] text-error">Failed</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-surface-secondary border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error hover:text-white hover:border-error"
                  >
                    <X size={10} />
                  </button>
                </div>
                {tokens !== null && (
                  <span className="text-[10px] text-text-tertiary mt-0.5">
                    {formatTokenEstimate(tokens)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Drop zone overlay */}
      {isDragging && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-accent bg-accent/5 text-xs font-medium text-accent"
        >
          Drop files here
        </div>
      )}

      {/* Controls row above the input (e.g. plan-mode toggle). The model is
          fixed at organisation level, so there is no per-thread picker. */}
      {trailingControl && (
        <div className="flex items-center gap-2 px-3 pt-2 pb-1">
          <div className="ml-auto">{trailingControl}</div>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2 p-3">
        <button
          type="button"
          onClick={openFilePicker}
          disabled={disabled || attachments.length >= MAX_FILES_PER_MESSAGE}
          className="flex-shrink-0 text-text-tertiary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed p-1"
          title="Attach file"
        >
          <Paperclip size={16} />
        </button>
        <input {...fileInputProps} />
        <DrivePickerButton
          accept={ACCEPTED_CONTENT_TYPES.join(",")}
          disabled={disabled || attachments.length >= MAX_FILES_PER_MESSAGE}
          label="Drive"
          onSignedId={addDriveAttachment}
        />
        {isSupported && (
          <button
            type="button"
            onClick={isListening ? stop : start}
            disabled={disabled}
            className={`flex-shrink-0 p-1 disabled:opacity-50 disabled:cursor-not-allowed ${
              isListening
                ? "text-accent animate-pulse"
                : "text-text-tertiary hover:text-text-primary"
            }`}
            title={isListening ? "Stop listening" : "Voice input"}
          >
            <Mic size={16} />
          </button>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          autoCorrect="on"
          spellCheck
          rows={1}
          className="flex-1 resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSend}
          className="flex-shrink-0 rounded-md bg-accent p-2 text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
